import { hasSomeData, InsightSeverity, IRetirementScoreCalculationsOutput, runRetirementScoreCalculations } from 'app/calcs/Accountant'
import { IncomeAndExpenseCalculations } from 'app/calcs/Accountant/Transactions'
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import * as ExpenseCalculators from 'app/calcs/expenses'
import { MaterialInput } from 'app/components';
import { IQuestionsGroupProps } from 'app/components/questions/base'
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { getFidelityRetirementScoreChartConfig } from 'app/containers/PipelineSection/Results/charts/health'
import { getYearlyColumnChartConfig } from 'app/containers/PipelineSection/Results/charts/yearlyColumn'
import { AccountsActions } from 'app/redux/Accounts/actions'
import { deadBy, IRetirementPerson, Profile } from 'app/redux/Profile/model'
import { MiscService } from 'app/services/MiscService'
import * as utils from 'app/utils'
import * as Config from 'config'
import * as HighchartsMore from 'highcharts-more'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import * as ReactHighcharts from 'react-highcharts'

const debug = utils.makeDebug(true)

//add highcharts-more
HighchartsMore(ReactHighcharts.Highcharts)

interface IRetirementResultsState {
  isBusy: boolean,
  scoreOutputs: IRetirementScoreCalculationsOutput
  chartConfig: any,
  yearlyChartConfig: any,
  profile: Profile,
  downloadFilename: string,
  preparingDownload: boolean
}

class ResultsContainer extends QuestionsGroupContainer<IQuestionsGroupContainerProps, IRetirementResultsState> {

  state: IRetirementResultsState = {
    isBusy: true,
    scoreOutputs: null,
    chartConfig: getFidelityRetirementScoreChartConfig(0),
    yearlyChartConfig: getYearlyColumnChartConfig(null),
    profile: null,
    downloadFilename: null,
    preparingDownload: false
  }

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info') || null
      if (!profile) { return }

      this.setState({ isBusy: true, profile })

      debug('about to runRetirementScoreCalculations')
      runRetirementScoreCalculations(profile)
        .then((scoreOutputs) => {
          debug('Finished runRetirementScoreCalculations')
          const yearlyChartConfig = getYearlyColumnChartConfig(scoreOutputs.analysis.transactions)
          this.setState({ isBusy: false, scoreOutputs, yearlyChartConfig, downloadFilename: null })
        })

    })

  }

  onDownloadClick(e) {
    if (this.state.downloadFilename) {
      e.href = this.state.downloadFilename;
      return;
    }

    // filter out the stuff that should not be exported
    const rawTxs = _.filter(this.state.scoreOutputs.analysis.transactions, (tx) => !tx.doNotExport)

    // mutate the transactions a bit to identify the 'what' key as a category
    const txs = _.map(rawTxs, (tx) => {
      const cats = _.keysIn(tx.what)
      if (1 !== cats.length) {
        throw new Error('cannot handle complex transactions when doing upload')
      }
      const category = cats[0]
      return {
        when: tx.when,
        section: tx.section,
        source: tx.source,
        who: tx.who,
        amount: tx.what[category],
        category,
        description: tx.description
      };
    })

    const endpoint = Config.getWebServicesConfig().miscEndpoint
    const svc = new MiscService(endpoint)
    const accessToken = AccountsActions.getSharedVar('accessToken')
    const filename = 'retirement-transactions-' + moment().format('MM-YYYY') + '.xlsx'
    this.setState({ preparingDownload: true })
    svc.exportTransactions(accessToken, filename, JSON.stringify(txs))
      .then((wr: any) => {
        const downloadFilename = endpoint + 'api/misc/downloadTransactionFile?path=' + wr.path
        this.setState({ downloadFilename })
      })
      .finally(() => this.setState({ preparingDownload: false }))
  }

  renderInsights() {
    if (this.state.scoreOutputs.insights.length) {
      const insightItems = _(this.state.scoreOutputs.insights)
        .orderBy((q) => q.severity)
        .map((e) => {
          const s = { __html: e.suggestion }
          return <li>{e.description} <b>Suggestion:</b> <span dangerouslySetInnerHTML={s}></span> (Impact: <b className="text-danger"  >{InsightSeverity[e.severity]}</b>)</li>
        })
        .value()

      return (
        <span>
          <h3>Insights</h3>
          <h5 className="lead">We've detected some issues with your current retirement plan...</h5>
          <ul className="status_insights">
            {insightItems}
          </ul>
        </span>
      )
    }
  }

  render() {
    if (this.state.isBusy) {
      return (
        <section>
          <h1>Retirement Status</h1>
          <h5 className="lead">We're performing some calculations...</h5>
          <img className="animated fadeIn" src="/assets/img/counting-money.gif" />
        </section>
      )
    }

    if (!this.state.scoreOutputs) { return null }

    const byMonths = _.groupBy(this.state.scoreOutputs.timeline, (tx: TX.ITransactionItem) => tx.when.format('MM-YYYY'))
    function getDescriptionMarkup(markup) { return { __html: markup } }

    const mutated = _(byMonths).keysIn().map((w) => {
      const details = byMonths[w].map((tx, key) =>
        <div key={key.toString()} dangerouslySetInnerHTML={getDescriptionMarkup(tx.description)} />
      )

      return {
        when: moment(w, 'MM-YYYY').format('MMMM, YYYY'),
        details
      }
    })
      .value()

    const averageMonthlyIncome = utils.toCurrency(this.state.scoreOutputs.analysis.averageMonthlyNetIncome)
    const totalIncome = utils.toCurrency(this.state.scoreOutputs.analysis.grossIncome)
    const hasScore = this.state.scoreOutputs.score !== 0
    ////debug('# scoreOutputs: ' + JSON.stringify(this.state.scoreOutputs))
    const somethingToShow = hasSomeData(this.state.scoreOutputs) && hasScore

    const timelineDetails = mutated.map((tx, index) =>
      <li key={index.toString()}>
        <div className="status_icon status_icon-check"></div>
        <div className="status_label">
          <h4>{tx.when}</h4>
          <p>{tx.details}</p>
        </div>
      </li>
    )

    return (
      <section>
        <h1>Retirement Summary</h1>
        {somethingToShow ?
          <article>
            {/*this.renderInsights()*/}
            <ReactHighcharts className="animated fadeIn" config={this.state.yearlyChartConfig} isPureConfig />
            { /*RA-123 {this.renderInsights()} */}

            <br />
            <p>Note that all numbers in this tool are in today’s dollars (assumed inflation of 2.5% has been removed from projections, where appropriate).</p>

            <h3>Additional Details</h3>

            <p>A detailed list of your retirement transactions, in Excel format, is available. You, or your financial planner, may
              find it useful. This will include all of the information that you have provided for retirement income and expenses.
            {!this.state.downloadFilename &&
                <span>
                  {this.state.preparingDownload ? <span> Preparing download <i className="fa fa-fw fa-spin fa-circle-o-notch" /></span>
                    : <span> Click <a onClick={this.onDownloadClick.bind(this)}><b>here</b></a> to prepare this file.</span>
                  }
                </span>
              }

              {this.state.downloadFilename &&
                <span> The transactions file is now available. <br />
                  <a href={this.state.downloadFilename} className="btn btn-primary"> Download file <i className="fa fa-download"></i></a>
                </span>
              }
            </p>

            <br />
            <h3>Plan Timeline</h3>
            <ul className="status_timeline">
              {timelineDetails}
            </ul>
          </article>
          :
          <h5 className="lead">You haven't entered enough information yet to allow us to calculate your retirement score. <a href="#/dashboard">Go to your dashboard</a> to start entering your information.</h5>
        }

      </section >
    )
  }
}

const Results = () => (
  <ResultsContainer
    header="Retirement Intro" icon="" />
)

interface IResultsSection {
  Results: () => React.ReactElement<IQuestionsGroupProps>
}

const ResultsSection: IResultsSection = {
  Results,
}

type ResultsKeys = keyof IResultsSection

export {
  Results,
  ResultsSection,
  ResultsKeys
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Results/index.tsx